import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { Link } from 'react-router-dom';

export default function ProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [verificationStatus, setVerificationStatus] = useState({
    photoId: false,
    proofOfAddress: false,
    cscsCard: false,
    qualificationCertificates: false,
    dbsCheck: false,
    publicLiability: false,
    wasteCarrierLicence: false,
    canAcceptJobs: false
  });

  // File upload states
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'documents' | 'settings'>('documents');

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    email_notifications: true,
    sms_notifications: true,
    job_alerts: true,
    marketing_emails: false,
    availability_status: 'available'
  });

  const requiresCertifications = profile?.trade === 'Electrician' || profile?.trade === 'Plumber';
  const requiresWasteLicence = profile?.trade === 'Waste Removal';

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      console.log('📋 Loaded provider profile:', data);
      console.log('📸 Profile photo URL:', data.selfie_url || data.profile_photo_url);

      setProfile(data);
      
      // Load verification status
      if (data.verification_status) {
        setVerificationStatus(data.verification_status);
      } else {
        // Initialize verification status based on existing documents
        const newStatus = {
          photoId: !!data.photo_id_url,
          proofOfAddress: !!data.proof_of_address_url,
          cscsCard: !!data.cscs_card_url,
          qualificationCertificates: !!(data.qualification_certificates && data.qualification_certificates.length > 0),
          dbsCheck: !!data.dbs_check_url,
          publicLiability: !!data.public_liability_url,
          wasteCarrierLicence: !!data.waste_carrier_licence_url,
          canAcceptJobs: false
        };
        newStatus.canAcceptJobs = checkCanAcceptJobs(newStatus);
        setVerificationStatus(newStatus);
      }

      // Load settings
      if (data.settings) {
        setSettingsForm({ ...settingsForm, ...data.settings });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingDoc(docType);
    setUploading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (docType === 'qualificationCertificates') {
        // Handle multiple files
        const uploadedUrls: string[] = [...(profile.qualification_certificates || [])];
        
        for (const file of Array.from(files)) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${user.id}/certificates/${Date.now()}_${Math.random()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('provider-documents')
            .upload(fileName, file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('provider-documents')
            .getPublicUrl(fileName);

          uploadedUrls.push(publicUrl);
        }

        // Update verification status
        const newStatus = {
          ...verificationStatus,
          qualificationCertificates: true,
          canAcceptJobs: checkCanAcceptJobs({ ...verificationStatus, qualificationCertificates: true })
        };

        await supabase
          .from('provider_profiles')
          .update({ 
            qualification_certificates: uploadedUrls,
            verification_status: newStatus
          })
          .eq('user_id', user.id);

        setVerificationStatus(newStatus);
        setMessage({ type: 'success', text: 'Qualification certificates uploaded successfully!' });
      } else {
        // Handle single file
        const file = files[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${docType}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('provider-documents')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('provider-documents')
          .getPublicUrl(fileName);

        // Update verification status
        const newStatus = {
          ...verificationStatus,
          [docType]: true,
          canAcceptJobs: checkCanAcceptJobs({ ...verificationStatus, [docType]: true })
        };

        const updateField = 
          docType === 'photoId' ? 'photo_id_url' :
          docType === 'proofOfAddress' ? 'proof_of_address_url' :
          docType === 'cscsCard' ? 'cscs_card_url' : 
          docType === 'dbsCheck' ? 'dbs_check_url' :
          docType === 'publicLiability' ? 'public_liability_url' :
          docType === 'wasteCarrierLicence' ? 'waste_carrier_licence_url' : null;
        
        if (updateField) {
          await supabase
            .from('provider_profiles')
            .update({ 
              [updateField]: publicUrl,
              verification_status: newStatus
            })
            .eq('user_id', user.id);
        }

        setVerificationStatus(newStatus);
        const docNames: Record<string, string> = {
          photoId: 'Photo ID',
          proofOfAddress: 'Proof of Address',
          cscsCard: 'CSCS Card',
          dbsCheck: 'DBS Check',
          publicLiability: 'Public Liability Insurance',
          wasteCarrierLicence: 'Waste Carrier Licence'
        };
        setMessage({ type: 'success', text: `${docNames[docType]} uploaded successfully!` });
      }

      await loadProfile();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to upload document' });
    } finally {
      setUploadingDoc(null);
      setUploading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleDeleteDocument = async (docType: string, url?: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (docType === 'qualificationCertificates' && url) {
        // Remove specific certificate
        const updatedCerts = (profile.qualification_certificates || []).filter((cert: string) => cert !== url);
        
        await supabase
          .from('provider_profiles')
          .update({ 
            qualification_certificates: updatedCerts,
            verification_status: {
              ...verificationStatus,
              qualificationCertificates: updatedCerts.length > 0
            }
          })
          .eq('user_id', user.id);

        setMessage({ type: 'success', text: 'Certificate deleted successfully!' });
      } else {
        // Delete single document
        const updateField = 
          docType === 'photoId' ? 'photo_id_url' :
          docType === 'proofOfAddress' ? 'proof_of_address_url' :
          docType === 'cscsCard' ? 'cscs_card_url' : 
          docType === 'dbsCheck' ? 'dbs_check_url' :
          docType === 'publicLiability' ? 'public_liability_url' :
          docType === 'wasteCarrierLicence' ? 'waste_carrier_licence_url' : null;
        
        if (updateField) {
          await supabase
            .from('provider_profiles')
            .update({ 
              [updateField]: null,
              verification_status: {
                ...verificationStatus,
                [docType]: false
              }
            })
            .eq('user_id', user.id);

          setMessage({ type: 'success', text: 'Document deleted successfully!' });
        }
      }

      await loadProfile();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete document' });
    } finally {
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await supabase
        .from('provider_profiles')
        .update({ 
          settings: settingsForm
        })
        .eq('user_id', user.id);

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const checkCanAcceptJobs = (status: typeof verificationStatus) => {
    // Always require DBS check and public liability
    if (!status.dbsCheck || !status.publicLiability) return false;
    
    // For electricians and plumbers, also require CSCS and certificates
    if (requiresCertifications) {
      return status.cscsCard && status.qualificationCertificates && status.dbsCheck && status.publicLiability;
    }
    
    // For waste removal, require waste carrier licence
    if (requiresWasteLicence) {
      return status.wasteCarrierLicence && status.dbsCheck && status.publicLiability;
    }
    
    // For other trades, DBS and public liability are required
    return true;
  };

  const viewDocument = (url: string) => {
    window.open(url, '_blank');
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    const requiredDocs = [
      profile.selfie_url,
      profile.photo_id_url,
      profile.proof_of_address_url,
      profile.cscs_card_url,
      profile.dbs_check_url,
      profile.public_liability_url
    ];

    // Add trade-specific requirements
    if (profile.trade === 'waste') {
      requiredDocs.push(profile.waste_carrier_licence_url);
    }
    if (profile.trade === 'electrician' || profile.trade === 'plumber') {
      requiredDocs.push(profile.qualifications_url);
    }

    const uploaded = requiredDocs.filter(Boolean).length;
    return Math.round((uploaded / requiredDocs.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
              activeTab === 'documents'
                ? 'bg-teal-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-file-list-3-line mr-2"></i>
            Documents & Verification
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
              activeTab === 'settings'
                ? 'bg-teal-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-settings-3-line mr-2"></i>
            Settings & Preferences
          </button>
        </div>
      </div>

      {/* Verification Status Alert */}
      {activeTab === 'documents' && !verificationStatus.canAcceptJobs && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
          <div className="flex gap-3">
            <i className="ri-alert-line text-orange-600 text-2xl flex-shrink-0"></i>
            <div className="flex-1">
              <h3 className="font-bold text-orange-900 mb-2">Complete Your Verification</h3>
              <p className="text-sm text-orange-800 mb-3">
                You need to upload the following documents before you can accept jobs:
              </p>
              <ul className="space-y-2 text-sm text-orange-800">
                {!verificationStatus.photoId && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">Photo ID (Required for all providers)</span>
                  </li>
                )}
                {!verificationStatus.proofOfAddress && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">Proof of Address (Required for all providers)</span>
                  </li>
                )}
                {!verificationStatus.cscsCard && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">CSCS Card (Required for all providers)</span>
                  </li>
                )}
                {!verificationStatus.dbsCheck && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">DBS Check (Required for all providers)</span>
                  </li>
                )}
                {!verificationStatus.publicLiability && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">Public Liability Insurance (Required for all providers)</span>
                  </li>
                )}
                {requiresCertifications && !verificationStatus.qualificationCertificates && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">Qualification Certificates (Required for {profile?.trade}s)</span>
                  </li>
                )}
                {requiresWasteLicence && !verificationStatus.wasteCarrierLicence && (
                  <li className="flex items-center gap-2">
                    <i className="ri-close-circle-line text-orange-600"></i>
                    <span className="font-medium">Waste Carrier Licence (Required for Waste Removal)</span>
                  </li>
                )}
              </ul>
              <p className="text-sm text-orange-800 mt-3 font-medium">
                Upload these documents below to start accepting jobs!
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documents' && verificationStatus.canAcceptJobs && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex gap-3">
            <i className="ri-checkbox-circle-line text-green-600 text-2xl flex-shrink-0"></i>
            <div>
              <h3 className="font-bold text-green-900 mb-1">Verification Complete!</h3>
              <p className="text-sm text-green-800">
                You can now accept jobs on the platform. All required documents have been verified.
              </p>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'} border-2 rounded-xl p-4`}>
          <div className="flex items-center gap-2">
            <i className={`${message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-xl`}></i>
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <>
          {/* Quick Document Overview Cards */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-folder-open-line text-teal-600"></i>
                  My Documents
                </h2>
                <p className="text-sm text-gray-600 mt-1">Quick access to all your uploaded documents</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-teal-600">{calculateCompletion()}%</div>
                <div className="text-xs text-gray-600">Complete</div>
              </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {/* Photo ID Card */}
              <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.photo_id_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.photo_id_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-id-card-line text-lg ${
                      profile?.photo_id_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">Photo ID</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.photo_id_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.photo_id_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.photo_id_url ? (
                    <button
                      onClick={() => window.open(profile.photo_id_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('photo_id')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* Proof of Address Card */}
              <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.proof_of_address_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.proof_of_address_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-home-line text-lg ${
                      profile?.proof_of_address_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">Proof of Address</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.proof_of_address_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.proof_of_address_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.proof_of_address_url ? (
                    <button
                      onClick={() => window.open(profile.proof_of_address_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('proof_of_address')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* CSCS Card */}
              <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.cscs_card_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.cscs_card_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-bank-card-line text-lg ${
                      profile?.cscs_card_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">CSCS Card</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.cscs_card_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.cscs_card_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.cscs_card_url ? (
                    <button
                      onClick={() => window.open(profile.cscs_card_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('cscs_card')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* DBS Check Card */}
              <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.dbs_check_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.dbs_check_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-shield-check-line text-lg ${
                      profile?.dbs_check_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">DBS Check</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.dbs_check_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.dbs_check_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.dbs_check_url ? (
                    <button
                      onClick={() => window.open(profile.dbs_check_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('dbs_check')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* Public Liability Card */}
              <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.public_liability_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.public_liability_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-shield-line text-lg ${
                      profile?.public_liability_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">Insurance</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.public_liability_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.public_liability_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.public_liability_url ? (
                    <button
                      onClick={() => window.open(profile.public_liability_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('public_liability')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* Waste Carrier Licence - Only for Waste Removal */}
              {profile?.trade === 'Waste Removal' && (
                <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                  profile?.waste_carrier_licence_url 
                    ? 'bg-green-50 border-green-200 hover:shadow-md' 
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="text-center">
                    <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      profile?.waste_carrier_licence_url ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      <i className={`ri-file-list-3-line text-lg ${
                        profile?.waste_carrier_licence_url ? 'text-green-600' : 'text-orange-600'
                      }`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-xs mb-1">Waste Licence</h4>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      profile?.waste_carrier_licence_url 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {profile?.waste_carrier_licence_url ? '✓' : '✗'} Required
                    </span>
                    {profile?.waste_carrier_licence_url ? (
                      <button
                        onClick={() => window.open(profile.waste_carrier_licence_url, '_blank')}
                        className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFileUpload('waste_carrier_licence')}
                        className="mt-2 w-full px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Qualifications - Only for Electricians and Plumbers */}
              {(profile?.trade === 'Electrician' || profile?.trade === 'Plumber') && (
                <div className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                  profile?.qualifications_url 
                    ? 'bg-green-50 border-green-200 hover:shadow-md' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="text-center">
                    <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      profile?.qualifications_url ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <i className={`ri-award-line text-lg ${
                        profile?.qualifications_url ? 'text-green-600' : 'text-blue-600'
                      }`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-xs mb-1">Qualifications</h4>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      profile?.qualifications_url 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {profile?.qualifications_url ? '✓' : '○'} Optional
                    </span>
                    {profile?.qualifications_url ? (
                      <button
                        onClick={() => window.open(profile.qualifications_url, '_blank')}
                        className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFileUpload('qualifications')}
                        className="mt-2 w-full px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-folder-upload-line text-teal-600"></i>
                Required Documents
              </h2>
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600">Completion:</div>
                <div className="text-2xl font-bold text-teal-600">{calculateCompletion()}%</div>
              </div>
            </div>

            {/* Quick Overview Cards */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
              {/* Selfie - REQUIRED */}
              <div className={`p-3 rounded-lg border-2 ${profile.selfie_url ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`w-10 h-10 ${profile.selfie_url ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  <i className={`ri-user-smile-line text-white text-lg`}></i>
                </div>
                <p className="text-xs font-semibold text-gray-900 text-center mb-1">Selfie</p>
                <p className={`text-[10px] ${profile.selfie_url ? 'text-green-600' : 'text-red-600'} text-center mb-2`}>
                  {profile.selfie_url ? 'Uploaded' : 'Required'}
                </p>
                {profile.selfie_url ? (
                  <button
                    onClick={() => window.open(profile.selfie_url, '_blank')}
                    className="w-full py-1 px-2 bg-green-600 text-white rounded text-[10px] hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View
                  </button>
                ) : (
                  <button
                    onClick={() => document.getElementById('selfie-upload')?.click()}
                    className="w-full py-1 px-2 bg-red-600 text-white rounded text-[10px] hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Upload
                  </button>
                )}
              </div>

              {/* Photo ID - REQUIRED */}
              <div className={`p-3 rounded-lg border-2 ${profile.photo_id_url ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`w-10 h-10 ${profile.photo_id_url ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  <i className={`ri-id-card-line text-white text-lg`}></i>
                </div>
                <p className="text-xs font-semibold text-gray-900 text-center mb-1">Photo ID</p>
                <p className={`text-[10px] ${profile.photo_id_url ? 'text-green-600' : 'text-red-600'} text-center mb-2`}>
                  {profile.photo_id_url ? 'Uploaded' : 'Required'}
                </p>
                {profile.photo_id_url ? (
                  <button
                    onClick={() => window.open(profile.photo_id_url, '_blank')}
                    className="w-full py-1 px-2 bg-green-600 text-white rounded text-[10px] hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View
                  </button>
                ) : (
                  <button
                    onClick={() => document.getElementById('photo-id-upload')?.click()}
                    className="w-full py-1 px-2 bg-red-600 text-white rounded text-[10px] hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Upload
                  </button>
                )}
              </div>

              {/* Proof of Address - Required for all */}
              <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.proof_of_address_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.proof_of_address_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-home-4-line text-2xl ${
                      profile?.proof_of_address_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">Proof of Address</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.proof_of_address_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.proof_of_address_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.proof_of_address_url ? (
                    <button
                      onClick={() => window.open(profile.proof_of_address_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('proof_of_address')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* CSCS Card - Required for all */}
              <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.cscs_card_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.cscs_card_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-bank-card-line text-2xl ${
                      profile?.cscs_card_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">CSCS Card</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.cscs_card_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.cscs_card_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.cscs_card_url ? (
                    <button
                      onClick={() => window.open(profile.cscs_card_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('cscs_card')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* DBS Check - Required for all */}
              <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.dbs_check_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.dbs_check_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-shield-check-line text-2xl ${
                      profile?.dbs_check_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">DBS Check</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.dbs_check_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.dbs_check_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.dbs_check_url ? (
                    <button
                      onClick={() => window.open(profile.dbs_check_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('dbs_check')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* Public Liability Insurance - Required for all */}
              <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                profile?.public_liability_url 
                  ? 'bg-green-50 border-green-200 hover:shadow-md' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="text-center">
                  <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                    profile?.public_liability_url ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <i className={`ri-shield-star-line text-2xl ${
                      profile?.public_liability_url ? 'text-green-600' : 'text-red-600'
                    }`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs mb-1">Public Liability Insurance</h4>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    profile?.public_liability_url 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {profile?.public_liability_url ? '✓' : '✗'} Required
                  </span>
                  {profile?.public_liability_url ? (
                    <button
                      onClick={() => window.open(profile.public_liability_url, '_blank')}
                      className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFileUpload('public_liability')}
                      className="mt-2 w-full px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {/* Waste Carrier Licence - Required for waste removal only */}
              {profile?.trade === 'Waste Removal' && (
                <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                  profile?.waste_carrier_licence_url 
                    ? 'bg-green-50 border-green-200 hover:shadow-md' 
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="text-center">
                    <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      profile?.waste_carrier_licence_url ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      <i className={`ri-delete-bin-line text-2xl ${
                        profile?.waste_carrier_licence_url ? 'text-green-600' : 'text-orange-600'
                      }`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-xs mb-1">Waste Licence</h4>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      profile?.waste_carrier_licence_url 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {profile?.waste_carrier_licence_url ? '✓' : '✗'} Required
                    </span>
                    {profile?.waste_carrier_licence_url ? (
                      <button
                        onClick={() => window.open(profile.waste_carrier_licence_url, '_blank')}
                        className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFileUpload('waste_carrier_licence')}
                        className="mt-2 w-full px-2 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Qualification Certificates - Required for electricians and plumbers */}
              {requiresCertifications && (
                <div className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                  profile?.qualification_certificates && profile.qualification_certificates.length > 0 
                    ? 'bg-green-50 border-green-200 hover:shadow-md' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="text-center">
                    <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      profile?.qualification_certificates && profile.qualification_certificates.length > 0 
                        ? 'bg-green-100' 
                        : 'bg-blue-100'
                    }`}>
                      <i className={`ri-award-line text-2xl ${
                        profile?.qualification_certificates && profile.qualification_certificates.length > 0 
                          ? 'text-green-600' 
                          : 'text-blue-600'
                      }`}></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-xs mb-1">Qualifications</h4>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      profile?.qualification_certificates && profile.qualification_certificates.length > 0 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {profile?.qualification_certificates && profile.qualification_certificates.length > 0 ? '✓' : '○'} Optional
                    </span>
                    {profile?.qualification_certificates && profile.qualification_certificates.length > 0 ? (
                      <button
                        onClick={() => window.open(profile.qualification_certificates[0], '_blank')}
                        className="mt-2 w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFileUpload('qualifications')}
                        className="mt-2 w-full px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Detailed Upload Sections */}
            <div className="space-y-4">
              {/* Selfie Upload - REQUIRED */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-smile-line text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Selfie Photo <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload a clear selfie photo of yourself. This helps us verify your identity and builds trust with clients.
                    </p>
                    
                    {profile.selfie_url ? (
                      <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
                            <div>
                              <p className="font-semibold text-gray-900">Selfie Uploaded</p>
                              <p className="text-sm text-gray-600">Your selfie has been uploaded successfully</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => window.open(profile.selfie_url, '_blank')}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteDocument('selfie_url')}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center bg-white">
                        <i className="ri-camera-line text-purple-400 text-4xl mb-4"></i>
                        <p className="text-gray-600 mb-4">Click to upload your selfie photo</p>
                        <input
                          id="selfie-upload"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'selfie_url')}
                          className="hidden"
                        />
                        <button
                          onClick={() => document.getElementById('selfie-upload')?.click()}
                          disabled={uploading}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {uploading ? 'Uploading...' : 'Upload Selfie'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Photo ID Upload - REQUIRED */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      profile?.photo_id_url 
                        ? 'bg-green-500' 
                        : 'bg-red-500'
                    }`}>
                      <i className={`ri-id-card-line text-white text-2xl ${
                      profile?.photo_id_url 
                        ? 'ri-checkbox-circle-fill text-green-500' 
                        : 'ri-close-circle-line text-red-600'
                    }`}></i>
                    </div>
                    <div>
                      <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Photo ID
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-600">Passport or Driving Licence</p>
                    </div>
                  </div>
                  {profile?.photo_id_url && (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>

                {profile?.photo_id_url ? (
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <i className="ri-file-text-line text-2xl text-teal-600"></i>
                        <div>
                          <p className="font-medium text-gray-900">Photo ID Document</p>
                          <p className="text-sm text-gray-600">Uploaded and verified</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewDocument(profile.photo_id_url)}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                        >
                          <i className="ri-eye-line mr-1"></i>
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('photoId')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'photo_id')}
                        disabled={uploadingDoc === 'photo_id'}
                        className="hidden"
                      />
                      {uploadingDoc === 'photo_id' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">Upload Photo ID</p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Proof of Address - Required for all */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      verificationStatus.proofOfAddress ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`ri-home-4-line text-2xl ${
                        verificationStatus.proofOfAddress ? 'text-green-600' : 'text-gray-400'
                      }`}></i>
                    </div>
                    <div>
                      <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Proof of Address
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-600">Utility bill or bank statement (within 3 months)</p>
                    </div>
                  </div>
                  {verificationStatus.proofOfAddress && (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>

                {profile?.proof_of_address_url ? (
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-2xl text-teal-600"></i>
                      <div>
                        <p className="font-medium text-gray-900">Proof of Address</p>
                        <p className="text-sm text-gray-600">Uploaded and verified</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDocument(profile.proof_of_address_url)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteDocument('proofOfAddress')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'proof_of_address')}
                        disabled={uploadingDoc !== null}
                        className="hidden"
                      />
                      {uploadingDoc === 'proof_of_address' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">Upload proof of address</p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* CSCS Card - Required for all */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      verificationStatus.cscsCard ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`ri-bank-card-line text-2xl ${
                        verificationStatus.cscsCard ? 'text-green-600' : 'text-gray-400'
                      }`}></i>
                    </div>
                    <div>
                      <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        CSCS Card
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-600">Required for all trades</p>
                    </div>
                  </div>
                  {verificationStatus.cscsCard && (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>

                {profile?.cscs_card_url ? (
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-2xl text-teal-600"></i>
                      <div>
                        <p className="font-medium text-gray-900">CSCS Card</p>
                        <p className="text-sm text-gray-600">Uploaded and verified</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDocument(profile.cscs_card_url)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteDocument('cscsCard')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'cscsCard')}
                        disabled={uploadingDoc !== null}
                        className="hidden"
                      />
                      {uploadingDoc === 'cscsCard' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">Upload CSCS card</p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* DBS Check - Required for all */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      verificationStatus.dbsCheck ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`ri-shield-check-line text-2xl ${
                        verificationStatus.dbsCheck ? 'text-green-600' : 'text-gray-400'
                      }`}></i>
                    </div>
                    <div>
                      <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        DBS Check
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-600">Must be Basic or Enhanced DBS issued within last 3 years</p>
                    </div>
                  </div>
                  {verificationStatus.dbsCheck && (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>

                {profile?.dbs_check_url ? (
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-2xl text-teal-600"></i>
                      <div>
                        <p className="font-medium text-gray-900">DBS Certificate</p>
                        <p className="text-sm text-gray-600">Uploaded and verified</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDocument(profile.dbs_check_url)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteDocument('dbsCheck')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'dbsCheck')}
                        disabled={uploadingDoc !== null}
                        className="hidden"
                      />
                      {uploadingDoc === 'dbsCheck' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">Upload DBS certificate</p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Public Liability Insurance - Required for all */}
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      verificationStatus.publicLiability ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`ri-shield-star-line text-2xl ${
                        verificationStatus.publicLiability ? 'text-green-600' : 'text-gray-400'
                      }`}></i>
                    </div>
                    <div>
                      <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        Public Liability Insurance
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-sm text-gray-600">Minimum £1 million coverage required</p>
                    </div>
                  </div>
                  {verificationStatus.publicLiability && (
                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Verified
                    </span>
                  )}
                </div>

                {profile?.public_liability_url ? (
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-2xl text-teal-600"></i>
                      <div>
                        <p className="font-medium text-gray-900">Insurance Certificate</p>
                        <p className="text-sm text-gray-600">Uploaded and verified</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDocument(profile.public_liability_url)}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteDocument('publicLiability')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(e, 'publicLiability')}
                        disabled={uploadingDoc !== null}
                        className="hidden"
                      />
                      {uploadingDoc === 'publicLiability' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">Upload insurance certificate</p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Waste Carrier Licence - Required for waste removal only */}
              {profile?.trade === 'Waste Removal' && (
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        verificationStatus.wasteCarrierLicence ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-delete-bin-line text-2xl ${
                          verificationStatus.wasteCarrierLicence ? 'text-green-600' : 'text-gray-400'
                        }`}></i>
                      </div>
                      <div>
                        <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          Waste Carrier Licence
                          <span className="text-red-500">*</span>
                        </label>
                        <p className="text-sm text-gray-600">Required for Waste Removal providers</p>
                      </div>
                    </div>
                    {verificationStatus.wasteCarrierLicence && (
                      <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                        <i className="ri-checkbox-circle-line mr-1"></i>
                        Verified
                      </span>
                    )}
                  </div>

                  {profile?.waste_carrier_licence_url ? (
                    <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <i className="ri-file-text-line text-2xl text-teal-600"></i>
                        <div>
                          <p className="font-medium text-gray-900">Waste Licence</p>
                          <p className="text-sm text-gray-600">Uploaded and verified</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewDocument(profile.waste_carrier_licence_url)}
                          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                        >
                          <i className="ri-eye-line mr-1"></i>
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteDocument('wasteCarrierLicence')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                      <label className="cursor-pointer block text-center">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload(e, 'wasteCarrierLicence')}
                          disabled={uploadingDoc !== null}
                          className="hidden"
                        />
                        {uploadingDoc === 'wasteCarrierLicence' ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                            <span className="text-sm text-gray-600">Uploading...</span>
                          </div>
                        ) : (
                          <>
                            <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                            <p className="text-sm text-gray-600 mb-1">Upload waste carrier licence</p>
                            <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB)</p>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Qualification Certificates - Required for electricians and plumbers */}
              {requiresCertifications && (
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        verificationStatus.qualificationCertificates ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <i className={`ri-award-line text-2xl ${
                          verificationStatus.qualificationCertificates ? 'text-green-600' : 'text-gray-400'
                        }`}></i>
                      </div>
                      <div>
                        <label className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          Qualification Certificates
                          <span className="text-red-500">*</span>
                        </label>
                        <p className="text-sm text-gray-600">Upload all relevant trade qualifications</p>
                      </div>
                    </div>
                    {verificationStatus.qualificationCertificates && (
                      <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                        <i className="ri-checkbox-circle-line mr-1"></i>
                        Verified
                      </span>
                    )}
                  </div>

                  {profile?.qualification_certificates && profile.qualification_certificates.length > 0 ? (
                    <div className="space-y-3 mb-4">
                      {profile.qualification_certificates.map((cert: string, index: number) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <i className="ri-file-text-line text-2xl text-teal-600"></i>
                            <div>
                              <p className="font-medium text-gray-900">Certificate {index + 1}</p>
                              <p className="text-sm text-gray-600">Uploaded and verified</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => viewDocument(cert)}
                              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                            >
                              <i className="ri-eye-line mr-1"></i>
                              View
                            </button>
                            <button
                              onClick={() => handleDeleteDocument('qualificationCertificates', cert)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer text-sm font-medium"
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-teal-500 transition-colors">
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        multiple
                        onChange={(e) => handleFileUpload(e, 'qualificationCertificates')}
                        disabled={uploadingDoc !== null}
                        className="hidden"
                      />
                      {uploadingDoc === 'qualificationCertificates' ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">
                            {profile?.qualification_certificates?.length > 0 ? 'Add more certificates' : 'Upload qualification certificates'}
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG or PDF (max 5MB each) - Multiple files allowed</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Profile Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-user-line text-teal-600"></i>
              Profile Information
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <p className="text-gray-900">{profile?.full_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trade</label>
                  <p className="text-gray-900">{profile?.trade}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <p className="text-gray-900">{profile?.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                  <p className="text-gray-900">{profile?.years_experience || profile?.experience_years} years</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <p className="text-gray-900">{profile?.address}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience Description</label>
                <p className="text-gray-900">{profile?.experience_description || profile?.experience}</p>
              </div>

              {profile?.certifications && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certifications</label>
                  <p className="text-gray-900">{profile.certifications}</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <i className={`${profile?.is_self_employed ? 'ri-checkbox-circle-line text-green-600' : 'ri-close-circle-line text-red-600'}`}></i>
                <span className="text-sm text-gray-700">Self-Employed Status</span>
              </div>

              {profile?.has_insurance && (
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-600"></i>
                  <span className="text-sm text-gray-700">Public Liability Insurance - {profile.insurance_provider}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <i className="ri-settings-3-line text-teal-600"></i>
            Account Settings & Preferences
          </h2>

          <div className="space-y-6">
            {/* Notification Preferences */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium text-gray-900">Email Notifications</label>
                    <p className="text-sm text-gray-600">Receive email updates about your jobs and account</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm({ ...settingsForm, email_notifications: !settingsForm.email_notifications })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      settingsForm.email_notifications ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settingsForm.email_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium text-gray-900">SMS Notifications</label>
                    <p className="text-sm text-gray-600">Get text messages for urgent updates</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm({ ...settingsForm, sms_notifications: !settingsForm.sms_notifications })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      settingsForm.sms_notifications ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settingsForm.sms_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium text-gray-900">Job Alerts</label>
                    <p className="text-sm text-gray-600">Get notified when new jobs match your trade</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm({ ...settingsForm, job_alerts: !settingsForm.job_alerts })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      settingsForm.job_alerts ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settingsForm.job_alerts ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-medium text-gray-900">Marketing Emails</label>
                    <p className="text-sm text-gray-600">Receive tips, news, and special offers</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm({ ...settingsForm, marketing_emails: !settingsForm.marketing_emails })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      settingsForm.marketing_emails ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settingsForm.marketing_emails ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Availability Status */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Availability Status</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="availability"
                    checked={settingsForm.availability_status === 'available'}
                    onChange={() => setSettingsForm({ ...settingsForm, availability_status: 'available' })}
                    className="w-5 h-5 text-teal-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Available</p>
                    <p className="text-sm text-gray-600">You can accept new jobs</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="availability"
                    checked={settingsForm.availability_status === 'busy'}
                    onChange={() => setSettingsForm({ ...settingsForm, availability_status: 'busy' })}
                    className="w-5 h-5 text-teal-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Busy</p>
                    <p className="text-sm text-gray-600">You're working on current jobs</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="availability"
                    checked={settingsForm.availability_status === 'unavailable'}
                    onChange={() => setSettingsForm({ ...settingsForm, availability_status: 'unavailable' })}
                    className="w-5 h-5 text-teal-600"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Unavailable</p>
                    <p className="text-sm text-gray-600">You won't receive new job offers</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legal & Policies Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal & Policies</h3>
        <div className="space-y-3">
          <Link
            to="/privacy-policy"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-teal-50 transition-colors">
                <i className="ri-shield-check-line text-xl text-gray-600 group-hover:text-teal-600"></i>
              </div>
              <span className="text-gray-700 font-medium">Privacy Policy</span>
            </div>
            <i className="ri-arrow-right-s-line text-xl text-gray-400 group-hover:text-teal-600"></i>
          </Link>
          <Link
            to="/provider-terms"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Provider Terms &amp; Conditions
            </span>
            <i className="ri-external-link-line text-gray-400 group-hover:text-teal-600"></i>
          </Link>
          <Link
            to="/payment-payout-policy"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Payment &amp; Payout Policy
            </span>
            <i className="ri-external-link-line text-gray-400 group-hover:text-teal-600"></i>
          </Link>
          <Link
            to="/cancellation-refund-policy"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Cancellation &amp; Refund Policy
            </span>
            <i className="ri-external-link-line text-gray-400 group-hover:text-teal-600"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}
